import type { PublicError } from "@nexra/contracts";
import { useTranslation } from "../i18n";
import { publicErrorMessage } from "../lib/error-message";
import { cn } from "../lib/utils";
import { Badge } from "./ui/badge";

export function PublicErrorSummary({ error, className }: Readonly<{ error?: PublicError; className?: string }>) {
  const { t } = useTranslation();
  const failure = error;
  const hasBody = failure && Object.hasOwn(failure, "upstreamError");
  const body = failure?.upstreamError;
  const root = body !== null && typeof body === "object" && !Array.isArray(body) ? body : undefined;
  const code = root?.code;
  const codeText = typeof code === "string" || typeof code === "number" || typeof code === "boolean" ? String(code) : undefined;
  const message = typeof body === "string" ? body
    : typeof root?.message === "string" ? root.message
      : typeof root?.error === "string" ? root.error : undefined;

  return <div className={cn("min-w-0", className)}>
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="m-0 text-sm font-semibold text-info">{t("requestHistory.platformError")}</h4>
        {failure?.nexraError.code ? <Badge tone="info" className="max-w-full break-all whitespace-normal font-mono">{failure?.nexraError.code}</Badge> : null}
      </div>
      <p className="m-0 whitespace-pre-wrap break-words text-sm leading-relaxed text-info">{publicErrorMessage(failure?.nexraError ?? { code: "internal_error" })}</p>
      {failure?.nexraError.param ? <p className="m-0 break-words text-sm text-info">{t("requestHistory.errorParam", { param: failure?.nexraError.param })}</p> : null}
    </div>
    <div className="mt-3 space-y-2 border-t border-border pt-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="m-0 text-sm font-semibold text-danger">{t(failure?.upstreamFormat === "decoded" ? "requestHistory.upstreamDecodedError" : "requestHistory.upstreamError")}</h4>
        {codeText?.trim() ? <Badge tone="danger" className="max-w-full break-all whitespace-normal font-mono">{codeText}</Badge> : null}
      </div>
      {hasBody ? message?.trim()
        ? <p className="m-0 whitespace-pre-wrap break-words text-sm leading-relaxed text-danger">{message}</p>
        : <p className="m-0 text-muted-foreground">{t("requestHistory.upstreamMessageMissing")}</p>
        : <p className="m-0 text-muted-foreground">{failure?.upstreamUnavailableReason ? t(`requestHistory.upstreamUnavailable.${failure.upstreamUnavailableReason}`) : t("requestHistory.upstreamErrorMissing")}</p>}
      {failure?.upstreamTruncated ? <p className="m-0 text-muted-foreground">{t("requestHistory.upstreamTruncated")}</p> : null}
    </div>
  </div>;
}
